import React from 'react';
import { View, Pressable, PanResponder } from 'react-native';
import Svg, { Path, Defs, Pattern, Circle, Rect } from 'react-native-svg';
import { useNav } from '../navigation/NavContext';
import { useHospitalityStore, tileDims, hasSavedFloorPositions, sectionBBox } from '../store/useHospitalityStore';
import { STATUS, STATUS_LABEL, useTheme, type TableStatus } from '../theme';
import { Card, CardHead, CardSub, CardBody } from '../components/Card';
import { Row, Col, Spread, Cluster, Grow, Screen } from '../components/Layout';
import Txt from '../components/Txt';
import Chip from '../components/Chip';
import Avatar from '../components/Avatar';
import Icon from '../components/Icon';
import Button, { buttonFg } from '../components/Button';
import Divider from '../components/Divider';
import { Input, Field } from '../components/Input';
import type { FloorTableState } from '../data/seed';
import { partySizeOf } from '../data/derive';
import { createWalkin, getWaitlist, seatWaitlistEntry, releaseWaitlistEntry, removeWaitlistEntry, getReservations } from '../services/reservations/reservation';
import { openFloorStream } from '../services/floor/floor';
import { updateFloorTable, getFloorTables, freeFloorTableWithoutEntry } from '../services/config/table';
import { settleOrder } from '../services/billing/billing';
import { showSuccess } from '../utils/successHandler';
import { handleApiError } from '../utils/errorHandler';

/**
 * Floor & Tables — ported from SK Hospitality.dc.html (template lines 230-330 +
 * floorVals(), lines 1455-1513). Faithful to the prototype: a fixed-coordinate
 * canvas with a dotted grid, dashed per-section zones behind the tables, and
 * tables positioned absolutely by pos_x/pos_y. Arrange mode drags tiles with an
 * 8px snap (react-native-web maps PanResponder onto mouse events), clamps to the
 * canvas, and persists to localStorage + PATCH pos_x/pos_y on drop.
 */

// Status soft fills used on the floor tokens (distinct from the tone SOFT map).
const SSOFT: Record<TableStatus, string> = {
  free: 'rgba(76,183,130,.13)', reserved: 'rgba(78,167,252,.13)', seated: 'rgba(240,136,62,.15)', dirty: 'rgba(235,87,87,.12)',
};

type BtnVariant = 'default' | 'accent';
interface ActionDef { label: string; icon: string; variant: BtnVariant; onClick: () => void; disabled?: boolean; }

const SNAP = 8;                 // drag grid, matches the prototype's round(/8)*8
const CANVAS_PAD = 48;          // breathing room below the lowest tile
const CANVAS_MIN_H = 320;
const GRID = 24;                // dotted-grid spacing (prototype: 24px)
const ZONE_HUES = [225, 150, 35]; // per-section dashed-zone hue (prototype)
const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
const tileRadius = (tb: FloorTableState, full: number) => (tb.shape === 'round' ? full : tb.communal ? 14 : 10);
const fmtWait = (seconds: number) => (seconds < 60 ? '<1m' : `${Math.round(seconds / 60)}m`);
const WAITLIST_POLL_MS = 5000;

/**
 * One table tile on the canvas. In Arrange mode it's a draggable View (8px snap,
 * dashed outline, grab cursor); otherwise a Pressable that selects the table.
 * Positions are read/written through the store's getState() so gesture closures
 * never go stale; the PanResponder is memoised on stable ids so a mid-drag
 * re-render doesn't rebuild it.
 */
function FloorTile({
  tb, editable, canvasW, canvasH, dim, selected, combineSrc, onSelect, onDrop,
}: {
  tb: FloorTableState; editable: boolean; canvasW: number; canvasH: number;
  dim: number; selected: boolean; combineSrc: boolean;
  onSelect: (id: string) => void; onDrop: (id: string) => void;
}) {
  const t = useTheme();
  const start = React.useRef({ x: tb.x, y: tb.y });
  const [dragging, setDragging] = React.useState(false);
  const { w, h } = tileDims(tb);
  // Read the latest canvas bounds through a ref rather than a PanResponder-memo
  // dependency: dragging a tile can itself grow canvasH (it's derived from the
  // lowest tile), and recreating the PanResponder mid-gesture severs the
  // in-progress touch/mouse capture — the drag would freeze the instant the
  // canvas grows. The ref keeps the responder identity stable for the whole
  // gesture while still seeing up-to-date bounds on every move.
  const boundsRef = React.useRef({ canvasW, canvasH });
  boundsRef.current = { canvasW, canvasH };
  const pan = React.useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => editable,
        onMoveShouldSetPanResponder: (_e, g) => editable && (Math.abs(g.dx) > 2 || Math.abs(g.dy) > 2),
        onPanResponderGrant: () => {
          const cur = useHospitalityStore.getState().tables.find((x) => x.id === tb.id);
          start.current = { x: cur?.x ?? tb.x, y: cur?.y ?? tb.y };
          setDragging(true);
        },
        onPanResponderMove: (_e, g) => {
          const { canvasW: cw, canvasH: ch } = boundsRef.current;
          const nx = clamp(Math.round((start.current.x + g.dx) / SNAP) * SNAP, 0, Math.max(0, cw - w));
          const ny = clamp(Math.round((start.current.y + g.dy) / SNAP) * SNAP, 0, Math.max(0, ch - h));
          useHospitalityStore.getState().moveTable(tb.id, nx, ny);
        },
        onPanResponderRelease: () => { setDragging(false); onDrop(tb.id); },
        onPanResponderTerminate: () => { setDragging(false); onDrop(tb.id); },
      }),
    [editable, tb.id, w, h, onDrop],
  );

  const color = STATUS[tb.status];
  let borderColor = color;
  let shadow = tb.status === 'seated' ? t.shadows.base : t.shadows.sm;
  if (selected) { borderColor = t.colors.accent; shadow = t.shadows.md; }
  if (combineSrc) { borderColor = t.colors.info; shadow = t.shadows.base; }
  if (dragging) { borderColor = t.colors.accent; shadow = t.shadows.lg; }

  const style = {
    position: 'absolute' as const,
    left: tb.x,
    top: tb.y,
    width: w,
    height: h,
    borderWidth: 2,
    borderColor,
    backgroundColor: SSOFT[tb.status],
    borderRadius: tileRadius(tb, t.radii.full),
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    gap: 1,
    paddingHorizontal: 6,
    opacity: dim,
    // Kill native text-selection / drag-and-drop so the pointer drag is clean
    // (otherwise the browser turns it into a text select / "copy" cursor).
    userSelect: 'none' as const,
    ...(editable
      ? { borderStyle: 'dashed' as const, cursor: dragging ? 'grabbing' : 'grab', zIndex: dragging ? 5 : 1, touchAction: 'none' as const }
      : null),
    ...shadow,
  };
  const inner = (
    <>
      <Txt mono weight="semibold" size="sm">{tb.label}</Txt>
      {tb.party && w >= 70
        ? <Txt color="ink3" size="xs" numberOfLines={1} center>{tb.party}</Txt>
        : <Txt color="ink4" size="xs">{tb.seats} seats</Txt>}
    </>
  );

  if (editable) return <View {...pan.panHandlers} style={style as object}>{inner}</View>;
  return <Pressable onPress={() => onSelect(tb.id)} style={style as object}>{inner}</Pressable>;
}

/** Dotted-grid backdrop (prototype: radial-gradient dots on 24px). */
function DotGrid({ w, h, color }: { w: number; h: number; color: string }) {
  if (w <= 0 || h <= 0) return null;
  return (
    <Svg width={w} height={h} style={{ position: 'absolute', top: 0, left: 0 }}>
      <Defs>
        <Pattern id="floorDots" width={GRID} height={GRID} patternUnits="userSpaceOnUse">
          <Circle cx={1} cy={1} r={1} fill={color} />
        </Pattern>
      </Defs>
      <Rect x={0} y={0} width={w} height={h} fill="url(#floorDots)" />
    </Svg>
  );
}

interface Zone { id: string; name: string; x: number; y: number; w: number; h: number; hue: number; muted: boolean }

/**
 * A dashed section zone. In Arrange mode the whole zone is draggable — grabbing
 * empty zone space moves every table in that section together (tiles on top keep
 * their own drag). On release the layout reflows so sections never overlap and the
 * section reorders to wherever it was dropped.
 */
function DraggableZone({ zone, editable, canvasW, canvasH, onDrop }: {
  zone: Zone; editable: boolean; canvasW: number; canvasH: number; onDrop: (sectionName: string) => void;
}) {
  const start = React.useRef<{ pos: Record<string, { x: number; y: number }>; min: { x: number; y: number }; max: { x: number; y: number } } | null>(null);
  // Same reasoning as FloorTile: dragging a whole section can itself grow
  // canvasH (it's derived from the lowest tile on the canvas), so the bound
  // must come from a ref, not a PanResponder-memo dependency — otherwise the
  // responder gets torn down and rebuilt mid-drag the moment the section
  // being dragged pushes canvasH taller, and the gesture appears to freeze.
  // This is exactly what made the bottom-most section ("stuck" after ~20px
  // of downward drag) unable to move further: every other section had more
  // headroom below it, so it never tripped the recreation; the bottom-most
  // one defines canvasH, so moving IT down retriggers the bug immediately.
  const boundsRef = React.useRef({ canvasW, canvasH });
  boundsRef.current = { canvasW, canvasH };
  const pan = React.useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => editable,
        onMoveShouldSetPanResponder: (_e, g) => editable && (Math.abs(g.dx) > 2 || Math.abs(g.dy) > 2),
        onPanResponderGrant: () => {
          const tiles = useHospitalityStore.getState().tables.filter((t) => !t.mergedInto && t.section === zone.name);
          const pos: Record<string, { x: number; y: number }> = {};
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          for (const t of tiles) {
            const d = tileDims(t);
            pos[t.id] = { x: t.x, y: t.y };
            minX = Math.min(minX, t.x); minY = Math.min(minY, t.y);
            maxX = Math.max(maxX, t.x + d.w); maxY = Math.max(maxY, t.y + d.h);
          }
          start.current = { pos, min: { x: minX, y: minY }, max: { x: maxX, y: maxY } };
        },
        onPanResponderMove: (_e, g) => {
          const st = start.current; if (!st) return;
          const { canvasW: cw, canvasH: ch } = boundsRef.current;
          const dx = Math.round(clamp(g.dx, -st.min.x, Math.max(0, cw - st.max.x)) / SNAP) * SNAP;
          const dy = Math.round(clamp(g.dy, -st.min.y, Math.max(0, ch - st.max.y)) / SNAP) * SNAP;
          const next: Record<string, { x: number; y: number }> = {};
          for (const id in st.pos) next[id] = { x: st.pos[id].x + dx, y: st.pos[id].y + dy };
          useHospitalityStore.getState().setTablePositions(next);
        },
        onPanResponderRelease: () => { start.current = null; onDrop(zone.name); },
        onPanResponderTerminate: () => { start.current = null; onDrop(zone.name); },
      }),
    [editable, zone.name, onDrop],
  );

  return (
    <View
      {...(editable ? pan.panHandlers : {})}
      pointerEvents={editable ? 'auto' : 'none'}
      style={{
        position: 'absolute',
        left: zone.x,
        top: zone.y,
        width: zone.w,
        height: zone.h,
        borderWidth: 1,
        borderStyle: 'dashed',
        borderColor: `oklch(0.62 0.05 ${zone.hue} / ${zone.muted ? 0.25 : 0.5})`,
        backgroundColor: `oklch(0.62 0.08 ${zone.hue} / ${zone.muted ? 0.02 : 0.05})`,
        borderRadius: 14,
        userSelect: 'none',
        ...(editable ? { cursor: 'grab', touchAction: 'none' } : null),
      } as object}
    >
      <Txt
        size="xs"
        upper
        weight="bold"
        style={{ position: 'absolute', left: 8, top: 4, letterSpacing: 0.8, color: `oklch(0.5 0.06 ${zone.hue})`, opacity: zone.muted ? 0.4 : 1, userSelect: 'none' } as object}
      >
        {zone.name}
      </Txt>
    </View>
  );
}

export default function Floor() {
  const { navigate } = useNav();
  const t = useTheme();
  const s = useHospitalityStore();
  const {
    tables, sections, waitlist, selectedTableId, combineFrom, editLayout, floorFilter,
  } = s;

  const filt = floorFilter || 'all';
  const combFrom = combineFrom ? tables.find((x) => x.id === combineFrom) : null;

  // Real walk-in details form — seating a walk-in with no queue entry used to
  // silently fabricate `{name:'Walk-in', phone:'+919999999999', partySize:2}`
  // for EVERY such seat, which (a) never captured the guest's actual party
  // size and (b) would resolve to the same fake CRM contact identity every
  // time. Opens inline in the selection card instead of seating immediately.
  const [walkinDraft, setWalkinDraft] = React.useState<{ tableId: string; name: string; phone: string; partySize: string } | null>(null);

  // Table-less waitlist join — for when every table is occupied/reserved and
  // staff just need to record the guest so they show up in the panel below,
  // with no table picked yet (that's what "Seat now" is for, later).
  const [joinWaitlistDraft, setJoinWaitlistDraft] = React.useState<{ name: string; phone: string; partySize: string } | null>(null);

  // Manual table override — "Seat now" auto-picks the best-fit free table by
  // default (one click, the common case), but VIPs/customer requests/ops
  // reasons sometimes need a SPECIFIC table instead. "Change table" on a
  // waitlist row enters this picking mode; tapping a free table on the floor
  // then seats that entry there via the selection card's action, instead of
  // the usual free-table actions.
  const [pickTableFor, setPickTableFor] = React.useState<{ id: string; name: string; size: number } | null>(null);

  // Measured width of the canvas — bounds/wraps the layout and drag clamping.
  const [canvasW, setCanvasW] = React.useState(0);
  const visibleTables = tables.filter((tb) => !tb.mergedInto);
  // Canvas grows to fit the lowest tile (prototype used a fixed 400px height).
  const canvasH = Math.max(
    CANVAS_MIN_H,
    visibleTables.reduce((mx, tb) => Math.max(mx, tb.y + tileDims(tb).h), 0) + CANVAS_PAD,
  );

  // On drop, mirror the new position to the adapter (pos_x/pos_y). Non-blocking:
  // the store already saved locally + toasted; a failed PATCH shouldn't undo the move.
  // Free placement: a table stays exactly where you drop it (no re-pack). Persist
  // the layout + mirror the moved tile's position to the adapter.
  const handleDrop = React.useCallback((id: string) => {
    useHospitalityStore.getState().persistPositions();
    const tb = useHospitalityStore.getState().tables.find((x) => x.id === id);
    if (tb?.apiId) {
      void updateFloorTable(tb.apiId, { posX: Math.round(tb.x), posY: Math.round(tb.y) }).catch(() => undefined);
    }
  }, []);

  // Whole-section drop → keep the dropped section exactly where it landed, but
  // push any section it now overlaps out of the way (AABB collision resolution;
  // see resolveSectionOverlaps) so two zones never overlap, then persist.
  // Kept referentially stable (reads canvasW through a ref) — DraggableZone's
  // PanResponder is memoised on this callback's identity, and a changing
  // identity mid-drag tears down the active gesture (see boundsRef there).
  const canvasWRef = React.useRef(canvasW);
  canvasWRef.current = canvasW;
  const handleSectionDrop = React.useCallback((sectionName: string) => {
    useHospitalityStore.getState().resolveSectionOverlaps(canvasWRef.current, sectionName);
  }, []);

  const hasOverlap = React.useCallback(() => {
    const vis = useHospitalityStore.getState().tables.filter((tb) => !tb.mergedInto);
    return vis.some((a, i) => {
      const ad = tileDims(a);
      return vis.slice(i + 1).some((b) => {
        const bd = tileDims(b);
        return a.x < b.x + bd.w && a.x + ad.w > b.x && a.y < b.y + bd.h && a.y + ad.h > b.y;
      });
    });
  }, []);

  // Auto-tidy ONLY a fresh floor: no layout saved yet AND the backend positions
  // arrive overlapping. Once anything is saved (a manual drag or a prior tidy), we
  // respect the operator's free placement and never re-pack on our own — use the
  // Reset button to re-tidy on demand.
  React.useEffect(() => {
    if (canvasW > 0 && !hasSavedFloorPositions() && hasOverlap()) s.autoArrange(canvasW);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasW]);

  // Real waitlist — replaces the local mock array with SK Queue's actual line
  // (queue_live via the adapter). Polled rather than pushed: sk-queue exposes
  // an SSE stream, but only to a browser holding a queue-scoped token, which
  // the staff FE doesn't have (it authenticates to the adapter, not to SK
  // Queue directly) — polling through the adapter's own auth is the simplest
  // correct fit here. A short interval keeps it close to live.
  const locationId = s.apiLocationId ?? s.location.apiId ?? '';
  const refetchWaitlist = React.useCallback(() => {
    if (!locationId) return;
    getWaitlist(locationId)
      .then((res) => {
        useHospitalityStore.getState().setWaitlist(
          res.entries.map((e) => ({ id: e.id, name: e.name, size: e.size, wait: fmtWait(e.waitedSeconds) })),
        );
      })
      .catch(() => undefined); // transient fetch errors just skip this tick
  }, [locationId]);
  React.useEffect(() => {
    if (!locationId) return;
    refetchWaitlist();
    const id = setInterval(refetchWaitlist, WAITLIST_POLL_MS);
    return () => clearInterval(id);
  }, [locationId, refetchWaitlist]);

  // If the entry being manually placed gets seated/removed from elsewhere
  // (another device, or this one's own confirm) the next poll drops it from
  // `waitlist` — clear the stale "choosing a table for…" banner instead of
  // leaving it pointed at an entry that no longer exists.
  React.useEffect(() => {
    if (pickTableFor && !waitlist.some((w) => w.id === pickTableFor.id)) setPickTableFor(null);
  }, [waitlist, pickTableFor]);

  // Real table status — without this, two staff devices only ever see each
  // other's seat/settle/free actions on a manual reload (the memo's step 6,
  // never implemented). Merges only the live-state fields (status/party/
  // since/entryId/orderId) by apiId — never touches position/section/shape,
  // so this can't fight a drag-in-progress or the layout editor.
  React.useEffect(() => {
    if (!locationId) return;
    let cancelled = false;
    const fetchTables = () => {
      // Fetch tables + reservations TOGETHER and fold the reservation-hold
      // reconciliation into the SAME state update as the physical-field
      // refresh below. Doing this as two sequential setState calls (fetch
      // tables → commit → fetch reservations → commit) produced a visible
      // flicker: a reserved table would render Free for one frame (physical
      // status only, reservation not applied yet) before flipping back to
      // Reserved once the second fetch resolved, every single poll tick.
      Promise.all([getFloorTables(locationId), getReservations().catch(() => null)])
        .then(([rows, reservations]) => {
          if (cancelled) return;
          const byApiId = new Map(rows.map((r) => [r.id, r]));
          // Only block a table once its reservation is actually imminent (same
          // venue-configurable lead as useHospitalityStore's applyReservationHolds,
          // Settings → Timing & turns) — a booking for hours later shouldn't sit
          // on the floor plan yet.
          const RESERVED_LEAD_MS = Math.max(0, useHospitalityStore.getState().settings.timing.reservedLeadMinutes) * 60 * 1000;
          const now = Date.now();
          const held = new Map<string, { name: string; size: number }>();
          if (reservations) {
            for (const r of reservations) {
              if (r.status !== 'confirmed' || !r.table) continue;
              if (new Date(r.time).getTime() - now > RESERVED_LEAD_MS) continue;
              for (const label of r.table.split('+')) held.set(label, r);
            }
          }
          useHospitalityStore.setState((st) => ({
            tables: st.tables.map((t) => {
              const fresh = t.apiId ? byApiId.get(t.apiId) : undefined;
              const merged = fresh
                ? {
                    ...t,
                    status: (fresh.status as TableStatus) || 'free',
                    party: fresh.party ?? null,
                    since: fresh.seatedSince ? new Date(fresh.seatedSince).toTimeString().slice(0, 5) : null,
                    entryId: fresh.activeEntryId ?? null,
                    orderId: fresh.activeOrderId ?? null,
                  }
                : t;
              if (!reservations) return merged; // reservations fetch failed — leave hold state as-is this tick
              const r = held.get(merged.label);
              if (r && merged.status === 'free') return { ...merged, status: 'reserved' as TableStatus, party: `${r.name} · ${r.size}` };
              if (!r && merged.status === 'reserved') return { ...merged, status: 'free' as TableStatus, party: null };
              return merged;
            }),
          }));
        })
        .catch(() => undefined); // transient fetch errors just skip this tick
    };
    // Server push (SSE), not a client poll — the adapter fires an 'invalidate'
    // ping on every real mutation (reservation create/cancel/reschedule, seat/
    // free) AND on a periodic server-side scan that catches a reservation
    // crossing into its reserved-lead window (nothing to click for that one —
    // see floorScheduler.ts). EventSource auto-reconnects on drop, same as the
    // dashboard's counter stream, so no fallback poll needed.
    const es = openFloorStream(fetchTables);
    // Still worth an immediate refetch on refocus — closes the small gap
    // between "tab was backgrounded when the SSE connection dropped" and
    // "EventSource has finished reconnecting."
    const onVisible = () => { if (typeof document === 'undefined' || !document.hidden) fetchTables(); };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
    if (typeof window !== 'undefined') window.addEventListener('focus', onVisible);
    return () => {
      cancelled = true;
      es.close();
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
      if (typeof window !== 'undefined') window.removeEventListener('focus', onVisible);
    };
  }, [locationId]);

  // Reset restores the saved SK layout; on the shared canvas those coords overlap,
  // so immediately re-tidy into bands (resetLayout's set() is synchronous).
  const handleReset = React.useCallback(() => {
    s.resetLayout();
    if (canvasW > 0 && hasOverlap()) s.autoArrange(canvasW);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasW, hasOverlap]);

  // Dashed section zones behind the tables — bounding box of each section's tiles
  // with a top-left label, hue per section (prototype floorZones()).
  const zones = sections
    .map((sec, i) => {
      const tbs = visibleTables.filter((tb) => tb.section === sec.name);
      const box = sectionBBox(tbs);
      if (!box) return null;
      const muted = filt !== 'all' && filt !== sec.name;
      return { id: sec.id, name: sec.name, ...box, hue: ZONE_HUES[i % 3], muted };
    })
    .filter(Boolean) as { id: string; name: string; x: number; y: number; w: number; h: number; hue: number; muted: boolean }[];

  // ── selection card ──
  const sel = tables.find((tb) => tb.id === selectedTableId) || null;
  const w0 = waitlist[0];

  // Seat a walk-in party at a free table. Two real cases, previously
  // conflated into one: if there's already a party waiting in the queue
  // (w0), this must SEAT THAT ENTRY (queue_seat) — it must never fabricate a
  // new join, or every click on a free table silently ate into SK Queue's
  // resource pool on top of whatever the operator actually intended. Only
  // when the queue is empty does this represent a genuine no-wait walk-in,
  // which still joins the queue (joinWalkin auto-allocates immediately when
  // a resource is free — see reservations.service.ts).
  // Both handlers below snapshot the full tables/waitlist arrays BEFORE the
  // optimistic local update, and restore that exact snapshot wholesale on a
  // backend failure (e.g. ERR_NO_FIT once the resource pool is full). A
  // partial revert (just re-adding the waitlist entry, say) previously left
  // the table falsely showing "Seated" even though the real seat never
  // happened — this is the fix for that.
  const handleSeatWalkIn = async (tableId: string, guest?: { name: string; phone: string; partySize: number }) => {
    const prevTables = tables;
    const prevWaitlist = waitlist;
    s.seatWalkIn(tableId, w0 ? w0.id : null, guest ? { name: guest.name, partySize: guest.partySize } : undefined);
    try {
      if (w0) {
        const res = await seatWaitlistEntry(w0.id, { tableId, party: `${w0.name} · ${w0.size}` });
        showSuccess(res, 'Walk-in seated');
      } else if (guest) {
        const res = await createWalkin({
          partySize: guest.partySize,
          guest: { name: guest.name, phone: guest.phone },
          locationId: s.apiLocationId ?? s.location.apiId ?? '',
          tableId,
        });
        showSuccess(res, 'Walk-in seated');
      }
    } catch (err) {
      handleApiError(err, 'Seat failed');
      useHospitalityStore.setState({ tables: prevTables, waitlist: prevWaitlist });
    }
  };

  // "Seat walk-in" button: if a real queue entry is waiting, its details are
  // already known — seat it directly. Otherwise this is a genuine walk-in
  // with no record yet, so collect their real name/phone/party size first
  // instead of fabricating one.
  const handleOpenSeatWalkIn = (tableId: string) => {
    if (w0) { void handleSeatWalkIn(tableId); return; }
    setWalkinDraft({ tableId, name: '', phone: '', partySize: '2' });
  };

  // Explicit "brand new walk-in" entry point — always opens the name/phone/
  // party-size form for THIS table, even when the queue isn't empty. Without
  // this, a non-empty waitlist made handleOpenSeatWalkIn always seat w0
  // instead, so staff had no way to seat a different walk-in party at a free
  // table while anyone was waiting.
  const handleOpenNewWalkIn = (tableId: string) => {
    setWalkinDraft({ tableId, name: '', phone: '', partySize: '2' });
  };

  // "Remove" on a waitlist row — guest left, no-show, or staff cancelled.
  // Uses removeWaitlistEntry, NOT releaseWaitlistEntry: release/queue_done
  // only works on an ALLOCATED/SERVING entry (settling a seated party) and
  // rejects ERR_NOT_RELEASABLE for a plain WAITING one, which is silently
  // swallowed as "already released" without actually clearing the entry —
  // exactly why a removed-looking party kept reappearing on this panel.
  // Refetches immediately so the row doesn't linger until the next poll tick.
  const handleRemoveFromWaitlist = async (entryId: string) => {
    const prevWaitlist = waitlist;
    useHospitalityStore.setState({ waitlist: waitlist.filter((w) => w.id !== entryId) });
    try {
      await removeWaitlistEntry(entryId);
      refetchWaitlist();
    } catch (err) {
      handleApiError(err, 'Could not remove from waitlist');
      useHospitalityStore.setState({ waitlist: prevWaitlist });
    }
  };

  const handleSubmitWalkinDraft = async () => {
    if (!walkinDraft) return;
    const partySize = Math.max(1, parseInt(walkinDraft.partySize, 10) || 1);
    const name = walkinDraft.name.trim() || 'Walk-in';
    const phone = walkinDraft.phone.trim();
    if (!phone) {
      handleApiError(new Error('Phone is required'), 'Cannot seat walk-in');
      return;
    }
    const tableId = walkinDraft.tableId;
    setWalkinDraft(null);
    await handleSeatWalkIn(tableId, { name, phone, partySize });
  };

  // "Add to waitlist" — joins SK Queue with no table (joinWalkin only seats
  // when a tableId is passed), so the guest just appears in the panel below
  // for staff to seat later via "Seat now". Refetches immediately after so
  // the new entry shows up without waiting for the next poll tick.
  const handleSubmitJoinWaitlist = async () => {
    if (!joinWaitlistDraft) return;
    const partySize = Math.max(1, parseInt(joinWaitlistDraft.partySize, 10) || 1);
    const name = joinWaitlistDraft.name.trim() || 'Walk-in';
    const phone = joinWaitlistDraft.phone.trim();
    if (!phone) {
      handleApiError(new Error('Phone is required'), 'Cannot add to waitlist');
      return;
    }
    setJoinWaitlistDraft(null);
    try {
      const res = await createWalkin({
        partySize,
        guest: { name, phone },
        locationId: s.apiLocationId ?? s.location.apiId ?? '',
      });
      showSuccess(res, 'Added to waitlist');
      refetchWaitlist();
    } catch (err) {
      handleApiError(err, 'Could not add to waitlist');
    }
  };

  // "Seat now" on a waitlist row → commit the real SK Queue lease (WAITING →
  // SERVING) so the entry doesn't reappear on the next waitlist poll, then
  // mirror the same optimistic local update the rest of the floor already
  // uses. If the backend call fails, undo the optimistic removal so the row
  // doesn't just vanish and silently pop back a few seconds later confused.
  const handleQuickSeat = async (wid: string) => {
    const prevTables = tables;
    const prevWaitlist = waitlist;
    const entry = waitlist.find((w) => w.id === wid);
    s.quickSeat(wid);
    try {
      // s.quickSeat picked a table and set it as selected — read that back so
      // the real table→entry link points at whichever table it actually chose.
      const tableId = useHospitalityStore.getState().selectedTableId;
      const link = tableId && entry ? { tableId, party: `${entry.name} · ${entry.size}` } : undefined;
      await seatWaitlistEntry(wid, link);
    } catch (err) {
      handleApiError(err, 'Seat failed');
      useHospitalityStore.setState({ tables: prevTables, waitlist: prevWaitlist });
    }
  };

  // Confirm a manually-picked table (pickTableFor) — same backend call as
  // "Seat now" (seatWaitlistEntry with an explicit link), just skipping the
  // auto-pick heuristic in favor of whatever table the host tapped. Reuses
  // s.seatWalkIn for the optimistic update since it already knows how to
  // move a waitlist entry onto an arbitrary table id.
  const handleSeatEntryAt = async (entryId: string, tableId: string) => {
    const prevTables = tables;
    const prevWaitlist = waitlist;
    const entry = waitlist.find((w) => w.id === entryId);
    if (!entry) return;
    s.seatWalkIn(tableId, entryId);
    setPickTableFor(null);
    try {
      await seatWaitlistEntry(entryId, { tableId, party: `${entry.name} · ${entry.size}` });
    } catch (err) {
      handleApiError(err, 'Seat failed');
      useHospitalityStore.setState({ tables: prevTables, waitlist: prevWaitlist });
    }
  };

  // Open (or start) this table's order and go build it. findOrCreateOrderForTable
  // reads/writes tb.orderId, so re-opening the same table always resumes the
  // same order instead of losing track of it.
  const handleViewOrder = (tableId: string) => {
    const tb = tables.find((t) => t.id === tableId);
    if (!tb) return;
    useHospitalityStore.getState().findOrCreateOrderForTable(tableId, tb.label, partySizeOf(tb));
    navigate('orders');
  };

  // Real settle: pay off the order's billing document (if one was ever
  // created — an order with zero items added has no real document to
  // settle), then release the real SK Queue entry seated here so SK Queue
  // frees the resource and allocates the next waiting party itself. Only on
  // success does the table clear locally — a failed settle must leave the
  // table exactly as it was, not silently show "free" while still unpaid.
  // Guards against a double-tap firing this twice before the first request
  // resolves — settleOrder tolerates that fine (stable idempotency key), but
  // releaseWaitlistEntry does not on its own, so the button itself must not
  // stay clickable mid-flight.
  const [settlingTableId, setSettlingTableId] = React.useState<string | null>(null);

  const handleMarkSettled = async (tableId: string) => {
    const tb = tables.find((t) => t.id === tableId);
    if (!tb) return;
    setSettlingTableId(tableId);
    try {
      if (tb.orderId && !tb.orderId.startsWith('ord_')) {
        const res = await settleOrder(tb.orderId, undefined, tb.entryId ?? undefined);
        showSuccess(res, 'Bill settled');
      }
      if (tb.entryId) {
        await releaseWaitlistEntry(tb.entryId, tableId);
      } else {
        // Direct walk-ins never get a queue entry, so releaseWaitlistEntry
        // (entryId-keyed) can never run for them — without this, the table
        // would settle its bill fine but never actually free.
        await freeFloorTableWithoutEntry(tableId);
      }
      s.markSettled(tableId);
    } catch (err) {
      handleApiError(err, 'Settle failed');
    } finally {
      setSettlingTableId(null);
    }
  };

  let selActions: ActionDef[] = [];
  if (sel) {
    if (sel.status === 'free' && pickTableFor) {
      // Manual override in progress (see "Change table" on a waitlist row) —
      // this free table's ONLY action is to confirm the pick; everything
      // else (combine, the usual w0/walk-in shortcut) is suppressed until
      // the host either confirms here or cancels.
      selActions = [
        { label: `Seat ${pickTableFor.name} · ${pickTableFor.size} here`, icon: 'users', variant: 'accent', onClick: () => { void handleSeatEntryAt(pickTableFor.id, sel.id); } },
        { label: 'Cancel', icon: 'x', variant: 'default', onClick: () => setPickTableFor(null) },
      ];
    } else if (sel.status === 'free') {
      selActions = w0
        ? [
            { label: `Seat ${w0.name} · ${w0.size} (queue)`, icon: 'users', variant: 'accent', onClick: () => handleOpenSeatWalkIn(sel.id) },
            { label: 'Seat walk-in', icon: 'user', variant: 'default', onClick: () => handleOpenNewWalkIn(sel.id) },
            { label: 'Combine with…', icon: 'combine', variant: 'default', onClick: () => s.startCombine(sel.id) },
          ]
        : [
            { label: 'Seat walk-in', icon: 'users', variant: 'accent', onClick: () => handleOpenNewWalkIn(sel.id) },
            { label: 'Combine with…', icon: 'combine', variant: 'default', onClick: () => s.startCombine(sel.id) },
          ];
    } else if (sel.status === 'reserved') {
      selActions = [
        { label: 'Seat the party', icon: 'users', variant: 'accent', onClick: () => s.seatReserved(sel.id) },
        { label: 'Combine with…', icon: 'combine', variant: 'default', onClick: () => s.startCombine(sel.id) },
      ];
    } else if (sel.status === 'seated') {
      selActions = [
        { label: 'View / build order', icon: 'receipt', variant: 'accent', onClick: () => handleViewOrder(sel.id) },
        { label: 'Mark bill settled → free', icon: 'check', variant: 'default', onClick: () => { void handleMarkSettled(sel.id); }, disabled: settlingTableId === sel.id },
        { label: 'Combine with…', icon: 'combine', variant: 'default', onClick: () => s.startCombine(sel.id) },
      ];
    } else {
      selActions = [{ label: 'Mark cleaned → free', icon: 'broom', variant: 'accent', onClick: () => s.markCleaned(sel.id) }];
    }
  }

  const legend: { label: string; color: string }[] = [
    { label: 'Free', color: STATUS.free }, { label: 'Reserved', color: STATUS.reserved },
    { label: 'Seated', color: STATUS.seated }, { label: 'Needs bussing', color: STATUS.dirty },
  ];
  const focusChips = [{ name: 'all', label: 'All sections' }, ...sections.map((sec) => ({ name: sec.name, label: sec.name }))];

  return (
    <Screen>
      <Row gap={16} style={{ alignItems: 'flex-start' }}>
        {/* left: floor plan (grows to fill) */}
        <Grow>
          {/* floor plan card */}
          <Card style={{ padding: 16 }}>
        {/* focus filter */}
        <Cluster gap={6} style={{ marginBottom: 12 }}>
          <Txt size="xs" upper weight="semibold" color="ink4" style={{ marginRight: 2 }}>Focus</Txt>
          {focusChips.map((f) => {
            const active = filt === f.name;
            return (
              <Pressable
                key={f.name}
                onPress={() => s.setFloorFilter(f.name)}
                style={{
                  paddingVertical: 5,
                  paddingHorizontal: 11,
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: active ? t.colors.accent : t.colors.line,
                  backgroundColor: active ? t.colors.accentSoft : t.colors.card,
                }}
              >
                <Txt size="sm" weight="medium" color={active ? 'accentInk' : 'ink3'}>{f.label}</Txt>
              </Pressable>
            );
          })}
        </Cluster>

        {/* legend + combine chip + arrange toggle */}
        <Spread style={{ marginBottom: 14, flexWrap: 'wrap', rowGap: 8 }}>
          <Cluster>
            {legend.map((l) => (
              <Row key={l.label} gap={6}>
                <View style={{ width: 10, height: 10, borderRadius: 3, backgroundColor: l.color }} />
                <Txt size="sm" color="ink3">{l.label}</Txt>
              </Row>
            ))}
          </Cluster>
          <Row gap={8}>
            {!!combineFrom && <Chip variant="info" dot>Combine mode — pick a table</Chip>}
            <Button
              size="sm"
              variant={editLayout ? 'accent' : 'default'}
              onPress={() => s.toggleEditLayout()}
            >
              <Svg viewBox="0 0 24 24" width={13} height={13} fill="none">
                <Path d="M5 9h4V5M19 15h-4v4M5 5l6 6M19 19l-6-6" stroke={buttonFg(editLayout ? 'accent' : 'default', t.colors)} strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" />
              </Svg>
              <Txt size="sm" weight="medium" color={buttonFg(editLayout ? 'accent' : 'default', t.colors)}>
                {editLayout ? 'Done arranging' : 'Arrange layout'}
              </Txt>
            </Button>
          </Row>
        </Spread>

        {editLayout && (
          <Row
            gap={8}
            style={{
              alignItems: 'flex-start',
              paddingVertical: 9,
              paddingHorizontal: 12,
              marginBottom: 12,
              backgroundColor: t.colors.accentSoft,
              borderWidth: 1,
              borderColor: t.colors.accentLine,
              borderRadius: 9,
            }}
          >
            <Svg viewBox="0 0 24 24" width={15} height={15} fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
              <Path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20" stroke={t.colors.accentInk} strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" />
            </Svg>
            <Grow>
              <Txt size="sm" color="accentInk">
                <Txt size="sm" weight="bold" color="accentInk">Arrange mode. </Txt>
                Drag any table to reposition it — snaps to grid, saves to{' '}
                <Txt size="sm" mono color="accentInk">hosp_floor_table.pos_x / pos_y</Txt>
                . Reset restores the saved SK layout.
              </Txt>
            </Grow>
            <Button size="xs" variant="default" onPress={handleReset}>Reset</Button>
          </Row>
        )}

        {/* floor plan — fixed-coordinate canvas (prototype): dotted grid, dashed
            per-section zones, tables positioned by pos_x/pos_y. Tap to select;
            drag to arrange in edit mode. */}
        <View
          onLayout={(e) => setCanvasW(e.nativeEvent.layout.width)}
          style={{
            position: 'relative',
            height: canvasH,
            borderWidth: 1,
            borderColor: t.colors.line,
            borderRadius: 12,
            backgroundColor: t.colors.paper2,
            overflow: 'hidden',
            userSelect: 'none',
          } as object}
        >
          <DotGrid w={canvasW} h={canvasH} color={t.colors.line} />

          {/* dashed section zones — draggable as a whole in Arrange mode */}
          {zones.map((z) => (
            <DraggableZone key={z.id} zone={z} editable={editLayout} canvasW={canvasW} canvasH={canvasH} onDrop={handleSectionDrop} />
          ))}

          {/* tables */}
          {visibleTables.map((tb) => {
            const filtered = filt !== 'all' && tb.section !== filt;
            const dimmed = filtered ? 0.22 : (!!combFrom && combFrom.id !== tb.id && combFrom.section !== tb.section ? 0.4 : 1);
            return (
              <FloorTile
                key={tb.id}
                tb={tb}
                editable={editLayout}
                canvasW={canvasW}
                canvasH={canvasH}
                dim={dimmed}
                selected={selectedTableId === tb.id}
                combineSrc={combineFrom === tb.id}
                onSelect={(id) => s.selectTable(id)}
                onDrop={handleDrop}
              />
            );
          })}
        </View>
          </Card>
        </Grow>

        {/* right column: selection details + waitlist (fixed width, prototype) */}
        <Col gap={14} style={{ width: 312, flexShrink: 0 }}>
      {/* selection card */}
      {sel && (
        <Card>
          <CardHead>
            <Row gap={10}>
              <View
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: SSOFT[sel.status],
                  borderWidth: 1.5,
                  borderColor: STATUS[sel.status],
                }}
              >
                <Txt mono weight="semibold" color={STATUS[sel.status]}>{sel.label}</Txt>
              </View>
              <Col gap={1}>
                <Txt weight="semibold">{sel.section}</Txt>
                <CardSub>{sel.seats} seats · {STATUS_LABEL[sel.status]}</CardSub>
              </Col>
            </Row>
            <Button variant="ghost" icon size="sm" onPress={() => s.clearSelect()}>
              <Icon name="x" size={14} strokeWidth={1.7} color={buttonFg('ghost', t.colors)} />
            </Button>
          </CardHead>
          <CardBody>
            <Col gap={12}>
              <Row gap={12} style={{ alignItems: 'flex-start' }}>
                <Col gap={2} style={{ flex: 1 }}>
                  <Txt size="xs" upper color="ink4">Party</Txt>
                  <Txt size="sm" weight="medium">{sel.party || '—'}</Txt>
                </Col>
                <Col gap={2} style={{ flex: 1 }}>
                  <Txt size="xs" upper color="ink4">Server</Txt>
                  <Txt size="sm" weight="medium">{sel.server}</Txt>
                </Col>
                <Col gap={2} style={{ flex: 1 }}>
                  <Txt size="xs" upper color="ink4">Since</Txt>
                  <Txt size="sm" weight="medium" mono>{sel.since || '—'}</Txt>
                </Col>
              </Row>
              <Divider />
              {walkinDraft && walkinDraft.tableId === sel.id ? (
                <Col gap={10}>
                  <Field label="Guest name">
                    <Input value={walkinDraft.name} onChangeText={(v) => setWalkinDraft((d) => (d ? { ...d, name: v } : d))} placeholder="e.g. Priya" autoFocus />
                  </Field>
                  <Field label="Phone">
                    <Input value={walkinDraft.phone} onChangeText={(v) => setWalkinDraft((d) => (d ? { ...d, phone: v } : d))} placeholder="+91…" keyboardType="phone-pad" />
                  </Field>
                  <Field label="Party size">
                    <Input value={walkinDraft.partySize} onChangeText={(v) => setWalkinDraft((d) => (d ? { ...d, partySize: v.replace(/[^0-9]/g, '') } : d))} keyboardType="number-pad" />
                  </Field>
                  <Row gap={8}>
                    <Button variant="accent" onPress={() => { void handleSubmitWalkinDraft(); }}>
                      <Icon name="check" size={15} strokeWidth={1.7} color={buttonFg('accent', t.colors)} />
                      <Txt size="md" weight="medium" color={buttonFg('accent', t.colors)}>Seat walk-in</Txt>
                    </Button>
                    <Button variant="ghost" onPress={() => setWalkinDraft(null)}>Cancel</Button>
                  </Row>
                </Col>
              ) : (
                selActions.map((a) => (
                  <Button key={a.label} block variant={a.variant} onPress={a.onClick} disabled={a.disabled}>
                    <Icon name={a.icon} size={15} strokeWidth={1.7} color={buttonFg(a.variant, t.colors)} />
                    <Txt size="md" weight="medium" color={buttonFg(a.variant, t.colors)}>{a.label}</Txt>
                  </Button>
                ))
              )}
            </Col>
          </CardBody>
        </Card>
      )}

      {/* waitlist */}
      <Card>
        <CardHead>
          <Row gap={8}>
            <Txt weight="semibold">Waitlist</Txt>
            <Chip variant="warn">{waitlist.length}</Chip>
          </Row>
          <Button
            variant="default"
            size="sm"
            onPress={() => setJoinWaitlistDraft({ name: '', phone: '', partySize: '2' })}
          >
            + Add to waitlist
          </Button>
        </CardHead>
        <CardBody>
          <Col gap={12}>
            {joinWaitlistDraft && (
              <Col gap={8} style={{ padding: 12, borderWidth: 1, borderColor: t.colors.line, borderRadius: 10 }}>
                <Field label="Name">
                  <Input value={joinWaitlistDraft.name} onChangeText={(v) => setJoinWaitlistDraft((d) => (d ? { ...d, name: v } : d))} placeholder="e.g. Priya" autoFocus />
                </Field>
                <Field label="Phone">
                  <Input value={joinWaitlistDraft.phone} onChangeText={(v) => setJoinWaitlistDraft((d) => (d ? { ...d, phone: v } : d))} placeholder="+91…" keyboardType="phone-pad" />
                </Field>
                <Field label="Party size">
                  <Input value={joinWaitlistDraft.partySize} onChangeText={(v) => setJoinWaitlistDraft((d) => (d ? { ...d, partySize: v.replace(/[^0-9]/g, '') } : d))} keyboardType="number-pad" />
                </Field>
                <Row gap={8}>
                  <Button variant="accent" onPress={() => { void handleSubmitJoinWaitlist(); }}>Add to waitlist</Button>
                  <Button variant="ghost" onPress={() => setJoinWaitlistDraft(null)}>Cancel</Button>
                </Row>
              </Col>
            )}
            {pickTableFor && (
              <Row gap={8} style={{ alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12, backgroundColor: t.colors.accentSoft, borderRadius: 10 }}>
                <Icon name="users" size={15} strokeWidth={1.7} color={t.colors.accentInk} />
                <Grow>
                  <Txt size="sm" weight="medium" color="accentInk">
                    Choosing a table for {pickTableFor.name} · {pickTableFor.size} — tap any free table on the floor plan.
                  </Txt>
                </Grow>
                <Button variant="ghost" size="sm" onPress={() => setPickTableFor(null)}>Cancel</Button>
              </Row>
            )}
            {waitlist.map((w) => (
              <Col key={w.id} gap={8} style={{ paddingVertical: 10, paddingHorizontal: 12, borderWidth: 1, borderColor: t.colors.line, borderRadius: 10 }}>
                <Row gap={10}>
                  <Avatar initials={w.name.slice(0, 2)} size="sm" />
                  <Col gap={1}>
                    <Txt size="md" weight="medium">{w.name} · {w.size}</Txt>
                    <Txt size="xs" color="ink3">waiting {w.wait}</Txt>
                  </Col>
                </Row>
                <Row gap={8}>
                  <Button variant="ghost" size="sm" style={{ flex: 1 }} onPress={() => setPickTableFor({ id: w.id, name: w.name, size: w.size })}>Change table</Button>
                  <Button variant="accent" size="sm" style={{ flex: 1 }} onPress={() => { void handleQuickSeat(w.id); }}>Seat now</Button>
                  <Button variant="ghost" icon size="sm" onPress={() => { void handleRemoveFromWaitlist(w.id); }}>
                    <Icon name="x" size={14} strokeWidth={1.7} color={buttonFg('ghost', t.colors)} />
                  </Button>
                </Row>
              </Col>
            ))}
            <Row gap={8} style={{ alignItems: 'flex-start', paddingVertical: 10, paddingHorizontal: 12, backgroundColor: t.colors.accentSoft, borderRadius: 10 }}>
              <View style={{ flexShrink: 0, marginTop: 1 }}>
                <Icon name="info" size={15} strokeWidth={1.7} color={t.colors.accentInk} />
              </View>
              <Grow>
                <Txt size="sm" color="accentInk" style={{ lineHeight: 18 }}>
                  <Txt size="sm" weight="bold" color="accentInk">One tap. </Txt>
                  “Seat now” auto-picks the best-fit free table — use “Change table” to pick a specific one instead (VIPs, requests, etc). Settle a seated table and SK Queue seats the next party automatically.
                </Txt>
              </Grow>
            </Row>
          </Col>
        </CardBody>
      </Card>
        </Col>
      </Row>
    </Screen>
  );
}
